import type { ProfileData } from '@/data/profile/use-profile.client';
import type { InboxV2SectionResponse } from '@/data/pull-requests/use-inbox-v2-pull-requests';
import type { PrV2Version } from '@/data/v2/pr/types';
import type { PrV2RouteContext } from '@/lib/pr-v2/route-utils';
import type { PrV2ReviewResponse, PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { User } from '@supabase/supabase-js';
import type { QueryClient } from '@tanstack/react-query';
import { PrefetchKind } from 'next/dist/client/components/router-reducer/router-reducer-types';
import type { useRouter } from 'next/navigation';

import { populatePrSummaryFromInbox } from '@/app/(home)/inbox/utils/populate-pr-summary-from-inbox';
import { calculatePrV2DefaultComparison } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/diff/hooks/use-pr-v2-version-comparison';
import { profileQueryKey } from '@/data/profile/use-profile.client';
import { inboxV2OpenPullRequestsQueryKeyPrefix } from '@/data/pull-requests/query-keys';
import { fetchPrV2Reviews, fetchPrV2Summary, fetchPrV2Versions } from '@/data/v2/pr/fetch-pr-v2';
import {
  PR_V2_STANDARD_STALE_TIME_MS,
  PR_V2_SUMMARY_STALE_TIME_MS,
} from '@/data/v2/pr/query-config';
import {
  prV2ReviewsQueryKey,
  prV2SummaryQueryKey,
  prV2VersionsQueryKey,
} from '@/data/v2/pr/query-keys';
import { prV2CodeChangesQueryOptions } from '@/data/v2/pr/use-pr-v2-code-changes';
import { parsePrV2Route } from '@/lib/pr-v2/route-utils';

/** The code-changes query a warm-up loaded, so a caller can keep observing that entry. */
export type WarmedPrDiff = ReturnType<typeof prV2CodeChangesQueryOptions>;

function parsePrHref(href: string): PrV2RouteContext | null {
  return parsePrV2Route(href.replace(/[?#].*$/, ''));
}

function findInboxPullRequest(queryClient: QueryClient, { owner, repo, number }: PrV2RouteContext) {
  return queryClient
    .getQueriesData<InboxV2SectionResponse>({ queryKey: inboxV2OpenPullRequestsQueryKeyPrefix() })
    .flatMap(([, data]) => data?.openPullRequests ?? [])
    .find(
      (pr) =>
        pr.repository.owner === owner && pr.repository.name === repo && `${pr.number}` === number,
    );
}

/** The login the PR page compares reviews against: the profile's, else the signed-in user's. */
function getViewerLogin(queryClient: QueryClient, viewer: User | null): string | null {
  const authUserName = viewer?.user_metadata?.user_name;
  return (
    queryClient.getQueryData<ProfileData>(profileQueryKey())?.userName?.trim() ||
    (typeof authUserName === 'string' && authUserName.trim()) ||
    null
  );
}

async function loadPrData(
  queryClient: QueryClient,
  pr: PrV2RouteContext,
  viewer: User | null,
): Promise<WarmedPrDiff | null> {
  const { owner, repo, number } = pr;
  const summaryKey = prV2SummaryQueryKey(owner, repo, number);
  const versionsKey = prV2VersionsQueryKey(owner, repo, number);
  const reviewsKey = prV2ReviewsQueryKey(owner, repo, number);
  const inboxPullRequest = findInboxPullRequest(queryClient, pr);
  if (inboxPullRequest) {
    populatePrSummaryFromInbox(queryClient, inboxPullRequest);
  }
  // The page opens on the cached summary, and one cached before a push still names the old head.
  const isSummaryCurrent =
    inboxPullRequest !== undefined &&
    queryClient.getQueryData<PrV2SummaryResponse>(summaryKey)?.pullRequest.headSha ===
      inboxPullRequest.head.sha;

  await Promise.all([
    isSummaryCurrent
      ? undefined
      : queryClient.prefetchQuery({
          queryKey: summaryKey,
          queryFn: ({ signal }) => fetchPrV2Summary(owner, repo, number, signal),
          // A summary naming another head than the inbox row is out of date, however recently
          // it was fetched.
          staleTime: inboxPullRequest ? 0 : PR_V2_SUMMARY_STALE_TIME_MS,
        }),
    queryClient.prefetchQuery({
      queryKey: versionsKey,
      queryFn: ({ signal }) => fetchPrV2Versions(owner, repo, number, signal),
      staleTime: PR_V2_STANDARD_STALE_TIME_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: reviewsKey,
      queryFn: ({ signal }) => fetchPrV2Reviews(owner, repo, number, signal),
      staleTime: PR_V2_STANDARD_STALE_TIME_MS,
    }),
  ]);

  const summary = queryClient.getQueryData<PrV2SummaryResponse>(summaryKey)?.pullRequest;
  // A closed or merged PR's code changes miss the materialized cache and take GitHub's slow path.
  if (summary?.state !== 'open' || summary.merged) {
    return null;
  }

  const comparison = calculatePrV2DefaultComparison({
    defaultBaseSha: summary.baseSha,
    defaultHeadSha: summary.headSha,
    versions: queryClient.getQueryData<PrV2Version[]>(versionsKey),
    reviews: queryClient.getQueryData<PrV2ReviewResponse[]>(reviewsKey),
    currentUserLogin: getViewerLogin(queryClient, viewer),
  });
  if (!comparison.baseSha || !comparison.headSha) {
    return null;
  }

  const diff = prV2CodeChangesQueryOptions(owner, repo, number, comparison);
  await queryClient.prefetchQuery(diff);
  return diff;
}

/**
 * Loads what the PR page at `href` reads first: its summary, the versions and reviews that pick
 * the comparison it shows (since your last review, for a PR you reviewed before), and that
 * comparison's code changes. Never ordered files or file descriptions, which can start LLM work.
 * Started without awaiting before a navigation, the page's own queries join these fetches.
 * Resolves with the code-changes query it loaded, if any.
 */
export async function warmPrData(
  queryClient: QueryClient,
  href: string,
  viewer: User | null,
): Promise<WarmedPrDiff | null> {
  const pr = parsePrHref(href);
  return pr ? loadPrData(queryClient, pr, viewer) : null;
}

/**
 * Warms the PR at `href` ahead of a click that usually follows within minutes: its data, and its
 * route in full so the navigation renders without a server round trip. A route prefetch lives
 * only minutes and a navigation cannot use one still in flight, so other opens warm only data.
 */
export async function warmPrForOpen(
  queryClient: QueryClient,
  router: Pick<ReturnType<typeof useRouter>, 'prefetch'>,
  href: string,
  viewer: User | null,
): Promise<void> {
  const pr = parsePrHref(href);
  if (pr) {
    router.prefetch(href, { kind: PrefetchKind.FULL });
    await loadPrData(queryClient, pr, viewer);
  }
}
